/**
 * 점수 + 콤보 상태 기계 (data-model §4, FR-005/006/007).
 * 순수 로직(pure functions)과 버스 배선(class)을 분리해 단위 테스트 가능하게 함.
 * 시간 소스는 주입된 게임 시각(Clock.now, timeScale 반영).
 */
import type { EventBus } from '../core/EventBus';
import type { Clock } from '../core/Clock';
import { BALANCE, type BalanceConfig } from '../config/balance';
import { getObjectDef } from '../config/objects';

export interface ComboState {
  combo: number;
  multiplier: number;
  score: number;
  maxCombo: number;
  lastBreakTime: number | null;
}

export function initialComboState(): ComboState {
  return { combo: 0, multiplier: 1.0, score: 0, maxCombo: 0, lastBreakTime: null };
}

/** 콤보 값 → 배수. 4콤보 이상은 마지막 배수 고정 (FR-006). */
export function multiplierFor(combo: number, multipliers: number[]): number {
  if (combo <= 0) return multipliers[0];
  const idx = Math.min(combo - 1, multipliers.length - 1);
  return multipliers[idx];
}

export interface BreakResult {
  state: ComboState;
  delta: number;
  /** 3콤보 이상 달성 순간 → 슬로모 (FR-007). 콤보가 3이 되는 그 파괴 포함(A2). */
  slowmo: boolean;
}

/** 파괴 1회 적용(순수). now: 게임 시각, baseScore: 오브젝트 기본 점수. */
export function applyBreak(
  prev: ComboState,
  now: number,
  baseScore: number,
  cfg: BalanceConfig = BALANCE,
): BreakResult {
  const withinWindow =
    prev.lastBreakTime !== null && now - prev.lastBreakTime <= cfg.comboWindowSec;
  const combo = withinWindow ? prev.combo + 1 : 1;
  const multiplier = multiplierFor(combo, cfg.comboMultipliers);
  const delta = Math.round(baseScore * multiplier);
  const state: ComboState = {
    combo,
    multiplier,
    score: prev.score + delta,
    maxCombo: Math.max(prev.maxCombo, combo),
    lastBreakTime: now,
  };
  return { state, delta, slowmo: combo >= cfg.slowmo.minCombo };
}

/** 콤보 만료 검사(순수). 마지막 파괴로부터 창 시간 초과 시 콤보 리셋. */
export function checkComboExpiry(
  prev: ComboState,
  now: number,
  cfg: BalanceConfig = BALANCE,
): { state: ComboState; expired: boolean } {
  if (prev.lastBreakTime === null) return { state: prev, expired: false };
  if (now - prev.lastBreakTime > cfg.comboWindowSec) {
    return {
      state: { ...prev, combo: 0, multiplier: 1.0, lastBreakTime: null },
      expired: true,
    };
  }
  return { state: prev, expired: false };
}

export class ScoreSystem {
  state: ComboState = initialComboState();

  constructor(
    private bus: EventBus,
    private clock: Clock,
  ) {
    this.bus.on('object:broken', (e) => this.onBroken(e.defId, e.impactSpeed));
    this.bus.on('session:reset', () => this.reset());
    this.bus.on('session:start', () => this.reset());
  }

  private onBroken(defId: string, impactSpeed: number): void {
    const def = getObjectDef(defId);
    // 세게 부술수록 가산: 임팩트속도/임계값 비율을 [1, max]로 클램프해 기본점수에 곱한다.
    const ratio = def.breakThreshold > 0 ? impactSpeed / def.breakThreshold : 1;
    const speedFactor = Math.min(BALANCE.scoreSpeedBonusMax, Math.max(1, ratio));
    const baseScore = def.baseScore * speedFactor;
    const res = applyBreak(this.state, this.clock.now, baseScore);
    this.state = res.state;
    this.bus.emit('score:changed', {
      score: this.state.score,
      delta: res.delta,
      combo: this.state.combo,
      multiplier: this.state.multiplier,
    });
    if (res.slowmo) {
      this.bus.emit('combo:slowmo', { combo: this.state.combo });
    }
  }

  /** 매 프레임 호출: 콤보 창 만료 검사. */
  update(): void {
    const res = checkComboExpiry(this.state, this.clock.now);
    if (res.expired) {
      this.state = res.state;
      this.bus.emit('combo:reset', {});
    }
  }

  reset(): void {
    this.state = initialComboState();
  }
}
